import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Client-side catalog of ALL sticker packs. Fetched once on app load.
 * Used to render `<sticker:pack/key>` tokens in chat messages regardless of
 * whether the reader owns the pack — anyone can SEE stickers, they just
 * can't POST from packs they don't own (that's enforced by the picker UI).
 */
let CATALOG_PROMISE = null;
let CATALOG_INDEX = null;   // pack_slug -> { pack_slug/sticker_key -> char }

async function loadCatalog() {
  if (!CATALOG_PROMISE) {
    CATALOG_PROMISE = api.get("/emotes/catalog")
      .then(r => {
        const idx = {};
        (r.data?.packs || []).forEach(p => {
          (p.stickers || []).forEach(s => {
            idx[`${p.slug}/${s.key}`] = s.char;
          });
        });
        CATALOG_INDEX = idx;
        return idx;
      })
      .catch(() => ({}));
  }
  return CATALOG_PROMISE;
}

// Regex that finds all `<sticker:pack/key>` tokens in a message string.
const STICKER_RE = /<sticker:([a-z0-9_-]+)\/([a-z0-9_-]+)>/gi;

/**
 * Parse a message string into an array of segments. Text segments are strings,
 * sticker segments are `{ type: "sticker", char }`.
 */
export function parseMessage(text, catalogIndex) {
  if (!text) return [];
  const out = [];
  let last = 0;
  let m;
  STICKER_RE.lastIndex = 0;
  while ((m = STICKER_RE.exec(text)) !== null) {
    if (m.index > last) out.push({ type: "text", value: text.slice(last, m.index) });
    const key = `${m[1]}/${m[2]}`.toLowerCase();
    const char = catalogIndex && catalogIndex[key];
    if (char) {
      out.push({ type: "sticker", char, key });
    } else {
      // Unknown / unloaded sticker — render token as-is so viewers know it was
      // meant to be a sticker.
      out.push({ type: "text", value: m[0] });
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ type: "text", value: text.slice(last) });
  return out;
}

/**
 * <ChatMessageText text="hello <sticker:starter/fire>" /> — renders text plus
 * inline stickers.
 */
export default function ChatMessageText({ text }) {
  const [catalog, setCatalog] = useState(CATALOG_INDEX);
  useEffect(() => {
    if (!catalog) loadCatalog().then(setCatalog);
  }, [catalog]);

  const segs = parseMessage(text, catalog);
  return (
    <span className="text-zinc-200 break-words">
      {segs.map((s, i) => s.type === "sticker" ? (
        <span
          key={i}
          className="inline-block align-middle mx-0.5 text-[22px] leading-none"
          title={s.key}
          data-testid={`sticker-inline-${s.key.replace("/", "-")}`}
        >
          {s.char}
        </span>
      ) : (
        <span key={i}>{s.value}</span>
      ))}
    </span>
  );
}
